/**
 * Three-stage validation pipeline: structural → semantic → normalization.
 */

import Ajv from "ajv";
import addFormats from "ajv-formats";
import {
  ERROR_CODES,
  POLL_KIND,
  VALIDATION_MODE,
  VOTE_VISIBILITY,
} from "./constants.js";
import { normalize, normalizeEmbeddedPoll } from "./normalize.js";
import { pollUploadSchema, addonPollInputSchema } from "./schema.js";
import { validateMaxSelections } from "../basic-polls/validate-poll.js";

// --- Ajv instance (reusable, strict mode) ---

const ajv = new Ajv({ strict: true, allErrors: true });
addFormats(ajv);
const compiledSchema = ajv.compile(pollUploadSchema);
const compiledAddonSchema = ajv.compile(addonPollInputSchema);

// --- Helpers ---

function makeFieldError(path, code, message) {
  return { path, code, message };
}

function makeWarning(path, code, message) {
  return { path, code, message };
}

// Minimal invitee format check (mirrors identifiers.js detection).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DISCORD_USERNAME_RE = /^[a-z0-9_.]{2,32}$/i;
const QS_USERNAME_RE = /^@.+$/;
const DISCORD_SNOWFLAKE_RE = /^\d{17,20}$/;
const LEGACY_DISCORD_TAG_RE = /^.+#\d{4}$/;

function isValidInviteeFormat(value) {
  if (QS_USERNAME_RE.test(value)) return true;
  if (EMAIL_RE.test(value)) return true;
  if (DISCORD_USERNAME_RE.test(value)) return true;
  return false;
}

function isRejectedInviteeFormat(value) {
  if (DISCORD_SNOWFLAKE_RE.test(value)) return "Discord IDs are not supported.";
  if (LEGACY_DISCORD_TAG_RE.test(value)) return "Legacy Discord tags (name#1234) are not supported.";
  return null;
}

// --- Stage 1: Structural Validation ---

export function validateStructural(input) {
  const errors = [];

  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push(makeFieldError("", ERROR_CODES.INVALID_JSON, "Input must be a JSON object."));
    return { valid: false, errors };
  }

  const valid = compiledSchema(input);
  if (!valid) {
    for (const err of compiledSchema.errors || []) {
      const path = err.instancePath
        ? err.instancePath.replace(/^\//, "").replace(/\//g, ".")
        : "";
      errors.push(
        makeFieldError(
          path,
          ERROR_CODES.SCHEMA_VALIDATION_ERROR,
          err.message || "Schema validation failed."
        )
      );
    }
    return { valid: false, errors };
  }

  return { valid: true, errors: [] };
}

// --- Stage 2: Semantic Validation ---

export function validateSemantic(input, mode = VALIDATION_MODE.PREFILL) {
  const errors = [];
  const warnings = [];
  const { pollKind, payload } = input;
  const now = new Date();

  if (pollKind === POLL_KIND.SESSION) {
    validateSessionSemantic(payload, mode, now, errors, warnings);
  } else if (pollKind === POLL_KIND.BASIC) {
    validateBasicSemantic(payload, mode, now, errors, warnings);
  }

  // Vote privacy constraint (applies to both kinds).
  if (
    payload.voteVisibility === VOTE_VISIBILITY.FULL &&
    payload.hideVoterIdentities === true
  ) {
    warnings.push(
      makeWarning(
        "payload.hideVoterIdentities",
        ERROR_CODES.AUTO_CORRECTED,
        "hideVoterIdentities forced to false because voteVisibility is full_visibility."
      )
    );
  }

  // Embedded polls inherit BASIC rules.
  if (pollKind === POLL_KIND.SESSION && Array.isArray(payload.embeddedPolls)) {
    for (let i = 0; i < payload.embeddedPolls.length; i++) {
      const prefix = `payload.embeddedPolls[${i}]`;
      const ep = payload.embeddedPolls[i];
      validateBasicSemanticFields(ep, prefix, mode, now, errors, warnings);

      if (
        ep.voteVisibility === VOTE_VISIBILITY.FULL &&
        ep.hideVoterIdentities === true
      ) {
        warnings.push(
          makeWarning(
            `${prefix}.hideVoterIdentities`,
            ERROR_CODES.AUTO_CORRECTED,
            "hideVoterIdentities forced to false because voteVisibility is full_visibility."
          )
        );
      }
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}

function validateSessionSemantic(payload, mode, now, errors, _warnings) {
  const slots = payload.slots || [];

  // NO_SLOTS is caught by structural schema (minItems: 1), but double-check.
  if (slots.length === 0) {
    errors.push(makeFieldError("payload.slots", ERROR_CODES.NO_SLOTS, "At least one slot is required."));
  }

  // Per-slot checks.
  const seen = new Set();
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i];
    const start = new Date(s.startUtcIso);
    const end = new Date(s.endUtcIso);

    if (end <= start) {
      errors.push(
        makeFieldError(
          `payload.slots[${i}].endUtcIso`,
          ERROR_CODES.SLOT_END_BEFORE_START,
          "Slot end time must be after start time."
        )
      );
    }

    if (mode === VALIDATION_MODE.CREATE && start < now) {
      errors.push(
        makeFieldError(
          `payload.slots[${i}].startUtcIso`,
          ERROR_CODES.SLOT_IN_PAST,
          "Slot start time must be in the future."
        )
      );
    }

    const key = `${s.startUtcIso}|${s.endUtcIso}`;
    if (seen.has(key)) {
      errors.push(
        makeFieldError(
          `payload.slots[${i}]`,
          ERROR_CODES.DUPLICATE_SLOT,
          "Duplicate slot detected."
        )
      );
    }
    seen.add(key);
  }

  // Invitee format checks.
  if (Array.isArray(payload.invitees)) {
    for (let i = 0; i < payload.invitees.length; i++) {
      const inv = payload.invitees[i];
      const rejected = isRejectedInviteeFormat(inv);
      if (rejected) {
        errors.push(
          makeFieldError(
            `payload.invitees[${i}]`,
            ERROR_CODES.INVALID_INVITEE_FORMAT,
            rejected
          )
        );
      } else if (!isValidInviteeFormat(inv)) {
        errors.push(
          makeFieldError(
            `payload.invitees[${i}]`,
            ERROR_CODES.INVALID_INVITEE_FORMAT,
            "Please enter a valid email address, Discord username, or @QSusername."
          )
        );
      }
    }
  }
}

function validateBasicSemantic(payload, mode, now, errors, warnings) {
  validateBasicSemanticFields(payload, "payload", mode, now, errors, warnings);

  // Group ID required for direct create.
  if (mode === VALIDATION_MODE.CREATE) {
    if (!payload.questingGroup?.id) {
      errors.push(
        makeFieldError(
          "payload.questingGroup.id",
          ERROR_CODES.MISSING_GROUP_ID,
          "questingGroup.id is required for direct creation."
        )
      );
    }
  }
}

function validateBasicSemanticFields(payload, prefix, mode, now, errors, _warnings) {
  const options = (payload.options || []).filter((o) => o.label?.trim());

  if (options.length < 2) {
    errors.push(
      makeFieldError(
        `${prefix}.options`,
        ERROR_CODES.TOO_FEW_OPTIONS,
        "At least two options with non-empty labels are required."
      )
    );
  }

  const settings = payload.settings || {};
  // Route through the SHARED basic-poll validator so the JSON-import surface
  // can't drift from the create/edit modals (2 ≤ max ≤ options + write-in).
  const maxSelResult = validateMaxSelections({
    voteType: settings.voteType,
    allowMultiple: settings.allowMultiple,
    allowWriteIn: settings.allowWriteIn,
    optionCount: options.length,
    maxSelections: settings.maxSelections,
  });
  if (!maxSelResult.valid) {
    errors.push(
      makeFieldError(
        `${prefix}.settings.maxSelections`,
        ERROR_CODES.INVALID_MAX_SELECTIONS,
        maxSelResult.message
      )
    );
  }

  if (
    mode === VALIDATION_MODE.CREATE &&
    settings.deadlineAt != null
  ) {
    const deadline = new Date(settings.deadlineAt);
    if (deadline < now) {
      errors.push(
        makeFieldError(
          `${prefix}.settings.deadlineAt`,
          ERROR_CODES.DEADLINE_IN_PAST,
          "Deadline must be in the future."
        )
      );
    }
  }
}

// --- Stage 3: Full Pipeline ---

/**
 * Validate and normalize a poll JSON upload payload.
 *
 * @param {unknown} input - Raw parsed JSON.
 * @param {string} mode - "prefill" or "create".
 * @returns {{ valid: boolean, errors: object[], warnings: object[], normalized: object|null }}
 */
export function validatePollJson(input, mode = VALIDATION_MODE.PREFILL) {
  // Stage 1: structural.
  const structural = validateStructural(input);
  if (!structural.valid) {
    return {
      valid: false,
      errors: structural.errors,
      warnings: [],
      normalized: null,
    };
  }

  // Stage 2: semantic.
  const semantic = validateSemantic(input, mode);
  if (!semantic.valid) {
    return {
      valid: false,
      errors: semantic.errors,
      warnings: semantic.warnings,
      normalized: null,
    };
  }

  // Stage 3: normalization.
  const normalized = normalize(input);

  return {
    valid: true,
    errors: [],
    warnings: semantic.warnings,
    normalized,
  };
}

// --- Addon poll validation ---

/**
 * Detect whether parsed JSON looks like a top-level poll envelope.
 * Returns "SESSION", "BASIC", or null.
 */
export function detectEnvelopeKind(parsed) {
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  if ("schemaVersion" in parsed || "pollKind" in parsed) {
    if (parsed.pollKind === POLL_KIND.SESSION) return "SESSION";
    if (parsed.pollKind === POLL_KIND.BASIC) return "BASIC";
    // Has envelope keys but unrecognized kind — still an envelope.
    return parsed.pollKind || "BASIC";
  }
  return null;
}

/**
 * Validate add-on poll JSON (no envelope, single object or array of embedded polls).
 *
 * @param {unknown} input - Raw parsed JSON.
 * @returns {{ valid: boolean, errors: object[], warnings: object[], normalized: object[]|null }}
 */
export function validateAddonPollJson(input) {
  // Hard-reject envelope JSON.
  const envelopeKind = detectEnvelopeKind(input);
  if (envelopeKind !== null) {
    return {
      valid: false,
      errors: [
        makeFieldError(
          "",
          ERROR_CODES.SCHEMA_VALIDATION_ERROR,
          `This looks like a ${envelopeKind} poll envelope. Add-on poll import expects raw poll object(s) without the schemaVersion/pollKind wrapper.`
        ),
      ],
      warnings: [],
      normalized: null,
    };
  }

  // Structural check against addonPollInputSchema.
  const valid = compiledAddonSchema(input);
  if (!valid) {
    const errors = [];
    for (const err of compiledAddonSchema.errors || []) {
      const path = err.instancePath
        ? err.instancePath.replace(/^\//, "").replace(/\//g, ".")
        : "";
      errors.push(
        makeFieldError(
          path,
          ERROR_CODES.SCHEMA_VALIDATION_ERROR,
          err.message || "Schema validation failed."
        )
      );
    }
    return { valid: false, errors, warnings: [], normalized: null };
  }

  // Normalize to array for uniform processing.
  const items = Array.isArray(input) ? input : [input];
  const errors = [];
  const warnings = [];
  const now = new Date();

  for (let i = 0; i < items.length; i++) {
    const prefix = items.length === 1 ? "" : `[${i}]`;
    const ep = items[i];
    validateBasicSemanticFields(ep, prefix, VALIDATION_MODE.PREFILL, now, errors, warnings);

    // Vote privacy warning per item.
    if (
      ep.voteVisibility === VOTE_VISIBILITY.FULL &&
      ep.hideVoterIdentities === true
    ) {
      warnings.push(
        makeWarning(
          `${prefix}${prefix ? "." : ""}hideVoterIdentities`,
          ERROR_CODES.AUTO_CORRECTED,
          "hideVoterIdentities forced to false because voteVisibility is full_visibility."
        )
      );
    }
  }

  if (errors.length > 0) {
    return { valid: false, errors, warnings, normalized: null };
  }

  // Normalize each via normalizeEmbeddedPoll.
  const normalized = items.map((ep, i) => normalizeEmbeddedPoll(ep, i));

  return { valid: true, errors: [], warnings, normalized };
}
